'use client';

import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import { EditorProvider, useCurrentEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Redo,
  RemoveFormatting,
  Save,
  SeparatorHorizontal,
  Strikethrough,
  TextQuote,
  Trash2,
  Undo,
  WrapText,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { cn } from '../../utils';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
} from '../ui';
import './styles.css';

function FormatButton({
  isActive,
  label,
  onClick,
  disabled,
}: {
  onClick: () => void;
  isActive?: boolean;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      className={cn(
        'min-h-[1.75rem] min-w-[1.75rem] rounded-sm border p-1',
        isActive ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
      )}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {label}
    </button>
  );
}

const MenuBar = () => {
  const { editor } = useCurrentEditor();
  const [open, setOpen] = useState(false);

  if (!editor) {
    return null;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleContent>
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-wrap gap-1">
            <FormatButton
              isActive={editor.isActive('bold')}
              label={<Bold size={16} />}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <FormatButton
              isActive={editor.isActive('italic')}
              label={<Italic size={16} />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <FormatButton
              isActive={editor.isActive('strike')}
              label={<Strikethrough size={16} />}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <FormatButton
              isActive={editor.isActive('code')}
              onClick={() => editor.chain().focus().toggleCode().run()}
              disabled={!editor.can().chain().focus().toggleCode().run()}
              label={<Code size={16} />}
            />
            <FormatButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              label={<List size={18} />}
              isActive={editor.isActive('bulletList')}
            />
            <FormatButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              label={<ListOrdered size={18} />}
              isActive={editor.isActive('orderedList')}
            />

            <FormatButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              label={<TextQuote size={16} />}
              isActive={editor.isActive('blockquote')}
            />
            <FormatButton
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              label={<SeparatorHorizontal size={16} />}
            />
            <FormatButton
              onClick={() => editor.chain().focus().setHardBreak().run()}
              label={<WrapText size={16} />}
            />
            <FormatButton
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              label={'code block'}
              isActive={editor.isActive('codeBlock')}
            />
          </div>
          <Separator orientation="horizontal" className="w-full" />
          <div className="flex h-full flex-wrap gap-1">
            <FormatButton
              label={<Pilcrow size={16} />}
              onClick={() => editor.chain().focus().setParagraph().run()}
              isActive={editor.isActive('paragraph')}
            />

            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              label={<Heading1 size={18} />}
              isActive={editor.isActive('heading', { level: 1 })}
            />
            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              label={<Heading2 size={18} />}
              isActive={editor.isActive('heading', { level: 2 })}
            />
            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
              label={<Heading3 size={18} />}
              isActive={editor.isActive('heading', { level: 3 })}
            />
            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 4 }).run()
              }
              label={<Heading4 size={18} />}
              isActive={editor.isActive('heading', { level: 4 })}
            />
            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 5 }).run()
              }
              label={<Heading5 size={18} />}
              isActive={editor.isActive('heading', { level: 5 })}
            />
            <FormatButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 6 }).run()
              }
              label={<Heading6 size={18} />}
              isActive={editor.isActive('heading', { level: 6 })}
            />
            <Separator
              orientation="vertical"
              className="mx-2 h-auto shrink-[1]"
            />
            <FormatButton
              onClick={() => editor.chain().focus().undo().run()}
              label={<Undo size={17} />}
            />
            <FormatButton
              onClick={() => editor.chain().focus().redo().run()}
              label={<Redo size={17} />}
            />
            <FormatButton
              label={<RemoveFormatting size={17} />}
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
            />
          </div>
        </div>
      </CollapsibleContent>
      <CollapsibleTrigger asChild>
        <Button className="mt-3 w-full py-0.5!" variant="secondary">
          {open ? 'Hide' : 'Show'} formatting tools
        </Button>
      </CollapsibleTrigger>
    </Collapsible>
  );
};

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({ types: [ListItem.name] } as any),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
  }),
];

export function RichTextEditor(props: {
  onSave: (html: string) => Promise<void>;
  content: string;
}): ReactNode {
  return (
    <div className="h-full w-full text-editor">
      <EditorProvider
        extensions={extensions}
        content={props.content}
        slotBefore={<MenuBar />}
        immediatelyRender={false}
      >
        <div className="mt-8 flex w-full justify-end gap-x-2">
          <EditorDiscardButton />
          <EditorSaveButton content={props.content} onSave={props.onSave} />
        </div>
      </EditorProvider>
    </div>
  );
}

export function EditorSaveButton(props: {
  onSave: (html: string) => Promise<void>;
  content: string;
}) {
  const { editor } = useCurrentEditor();
  const [saving, setSaving] = useState(false);
  console.log(props.content, editor?.getHTML());
  return (
    <Button
      onClick={async () => {
        setSaving(true);
        const content = editor?.getText() ? editor?.getHTML() : '';
        if (content || props.content !== editor?.getHTML()) {
          await props.onSave(content);
        }
        setSaving(false);
      }}
      variant="outline"
      loading={saving}
      disabled={props.content === editor?.getHTML()}
    >
      <div className="flex items-center justify-between">
        <Save size={16} />
        <p className="mt-0.5! ml-2">Save changes</p>
      </div>
    </Button>
  );
}

export function EditorDiscardButton() {
  const { editor } = useCurrentEditor();
  return (
    <Button
      onClick={() => {
        editor?.commands.setContent('');
      }}
      variant="outline"
      className="border border-red/50 bg-red/15 text-red hover:bg-red/20 hover:text-red"
      disabled={!editor?.getText()}
    >
      <div className="flex items-center justify-between ">
        <Trash2 size={16} />
        <p className="mt-0.5! ml-2">Discard</p>
      </div>
    </Button>
  );
}
